import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { DrawerContext } from "context/DrawerContext";

/**
 * Shared look for sidebar rows: inset, rounded, compact, with the current page
 * tinted (palette.navSelected). The inset plus padding keeps the icon at the same
 * spot on the mini rail. Text color is set explicitly because index.css pins
 * `a .MuiListItemText-primary` to near-black.
 */
export const navRowSx = {
  minHeight: 40,
  py: 0.5,
  // A 2px gap between rows, so neighboring rounded rows don't touch.
  my: "1px",
  mx: 1,
  px: 1.5,
  borderRadius: 2,
  // Focus too: MUI's own selected-and-focused rule is more specific than plain
  // .Mui-selected and paints primary (blue in dark mode) over the gold.
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    bgcolor: "navSelected.main",
    color: "navSelected.contrastText",
  },
  "&.Mui-selected .MuiListItemIcon-root": { color: "inherit" },
  "&.Mui-selected .MuiListItemText-primary": {
    color: "inherit",
    fontWeight: "medium",
  },
};

/** Sidebar tooltip: the name, plus the one-line explainer when there is one. */
export const NavHint = ({ text, hint, open, children }) => (
  <Tooltip
    placement="right"
    // The expanded row already shows the name, so only an explainer is worth a
    // tooltip. With nothing to say the title is empty, which MUI never shows: only
    // switching off hover still let focus (the row you just clicked) open a blank one.
    title={
      open && !hint ? (
        ""
      ) : (
        <>
          {!open && (
            <Box component="span" sx={{ display: "block", fontWeight: "medium" }}>
              {text}
            </Box>
          )}
          {hint}
        </>
      )
    }
  >
    {children}
  </Tooltip>
);

const NavListItem = ({
  icon,
  text,
  secondary,
  hint,
  link,
  onClick,
  disabled,
  noSelect,
  matchPathPrefix,
  end,
  sx,
}) => {
  const open = useContext(DrawerContext);
  const { pathname } = useLocation();
  let external = false;
  if (link) {
    const pattern = /^((http|https):\/\/)/;
    external = pattern.test(link);
  }

  // Active when the path matches the link exactly, or (with matchPathPrefix)
  // when it sits under the link as a sub-route.
  const selected =
    noSelect || !link
      ? false
      : pathname === link ||
        (matchPathPrefix && !external && pathname.startsWith(`${link}/`));

  const inner = (
    <>
      <ListItemIcon
        sx={[
          { minWidth: 0, justifyContent: "center" },
          open ? { mr: 2 } : { mr: 0 },
        ]}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={text}
        secondary={secondary}
        primaryTypographyProps={{ variant: "body2" }}
        secondaryTypographyProps={{ variant: "caption" }}
        // Gone, not just transparent, on the mini rail so it can't push the icon off center.
        sx={{ display: open ? "block" : "none", my: 0.25 }}
      />
      {/* Trailing control (e.g. a switch); the mini rail has no room for it. */}
      {open && end}
    </>
  );

  const button = (onClick || link) && (
    <ListItemButton
      selected={selected}
      sx={[
        navRowSx,
        // The mini rail is too narrow for the side padding plus a 24px icon.
        open ? { justifyContent: "initial" } : { justifyContent: "center", px: 0 },
      ]}
    >
      {inner}
    </ListItemButton>
  );

  const item = (
    <ListItem
      disablePadding
      onClick={onClick}
      disabled={disabled}
      sx={{ display: "block", ...sx }}
    >
      {button ? (
        <NavHint text={text} hint={hint} open={open}>
          {button}
        </NavHint>
      ) : (
        inner
      )}
    </ListItem>
  );

  // Internal link
  if (link && !external) return <Link to={link}>{item}</Link>;

  // External link
  if (link && external)
    return (
      <Link to={link} target="_blank" rel="noreferrer">
        {item}
      </Link>
    );

  return item;
};

export default NavListItem;
